import { createFileRoute, Link } from "@tanstack/react-router";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { listHabitacionesConFotos } from "@/lib/fotos.functions";
import { useSiteData } from "@/lib/use-site-data";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { TarifasSection } from "@/components/TarifasSection";
import salonAsset from "../assets/casa/salon.webp.asset.json";
import comedorAsset from "../assets/casa/comedor.webp.asset.json";
import cocinaAsset from "../assets/casa/cocina.webp.asset.json";
import banoAsset from "../assets/casa/bano.webp.asset.json";
import fachadaAsset from "../assets/casa/fachada.webp.asset.json";

const salon = salonAsset.url;
const exterior = fachadaAsset.url;
const comedor = comedorAsset.url;
const cocina = cocinaAsset.url;
const bano = banoAsset.url;

export const Route = createFileRoute("/alojamiento")({
  loader: () => listHabitacionesConFotos(),
  errorComponent: ({ error }) => <div className="mx-auto max-w-2xl px-4 py-20 text-center text-sm text-muted-foreground" role="alert">{error instanceof Error ? error.message : "No se pudo cargar el alojamiento"}</div>,
  notFoundComponent: () => <div className="mx-auto max-w-2xl px-4 py-20 text-center text-sm text-muted-foreground">No encontrado.</div>,
  head: ({ loaderData }) => ({
    meta: [
      { title: "Casa rural completa para 14 personas · Guadalajara" },
      { name: "description", content: "Casa rural completa en El Ordial (Guadalajara): 6 habitaciones con baño privado, salón con chimenea, cocina equipada y patio. Hasta 14 personas." },
      { property: "og:title", content: "Casa rural completa para 14 personas · Guadalajara" },
      { property: "og:description", content: "6 habitaciones con baño privado, salón con chimenea y cocina equipada. Sierra del Alto Rey, Guadalajara." },
      { property: "og:url", content: "/alojamiento" },
    ],
    links: [{ rel: "canonical", href: "https://elpajardebenito.es/alojamiento" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { label: "Inicio", to: "/" },
            { label: "Alojamiento", to: "/alojamiento" },
          ]),
        ),
      },
      ...(loaderData && loaderData.length > 0
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "ItemList",
                name: "Habitaciones de El Pajar de Benito",
                itemListElement: loaderData.map((h, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  item: {
                    "@type": "HotelRoom",
                    name: h.nombre,
                    ...(h.descripcion ? { description: h.descripcion } : {}),
                    occupancy: { "@type": "QuantitativeValue", maxValue: h.capacidad, unitCode: "C62" },
                    ...(h.fotos[0]?.url ? { image: h.fotos[0].url } : {}),
                    ...(h.precioDesde
                      ? {
                          offers: {
                            "@type": "Offer",
                            price: h.precioDesde,
                            priceCurrency: "EUR",
                            url: "https://elpajardebenito.es/reserva",
                            availability: "https://schema.org/InStock",
                          },
                        }
                      : {}),
                  },
                })),
              }),
            },
          ]
        : []),
    ],
  }),
  component: Alojamiento,
});

function Alojamiento() {
  const habitaciones = Route.useLoaderData();
  const { config, servicios } = useSiteData();
  return (
    <>
      <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Alojamiento" }]} />
      <section className="mx-auto max-w-4xl px-4 pt-20 pb-12 text-center md:px-6 md:pt-28">
        <p className="text-xs uppercase tracking-[0.28em] text-accent">El alojamiento</p>
        <h1 className="mt-4 font-serif text-4xl text-foreground md:text-5xl">
          La casa completa, pensada para grupos
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-muted-foreground">{config.alojamiento_intro}</p>
      </section>
      <section className="mx-auto max-w-6xl px-4 pb-20 md:px-6">
        <img src={salon} alt="Salón principal con vigas, piedra y chimenea" width={1600} height={1067} loading="lazy" className="aspect-[16/9] w-full rounded-sm object-cover" />
      </section>
      <section className="mx-auto max-w-6xl px-4 pb-20 md:px-6">
        <div className="grid gap-10 md:grid-cols-2 md:items-center md:gap-16">
          <img src={cocina} alt="Cocina equipada con horno de leña y barra de obra" width={1600} height={1067} loading="lazy" className="aspect-[4/3] w-full rounded-sm object-cover" />
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-accent">Zonas comunes</p>
            <h2 className="mt-3 font-serif text-3xl text-foreground md:text-4xl">Salón y cocina para compartir</h2>
            <p className="mt-4 text-muted-foreground">
              El alma de la casa: un salón amplio con chimenea de leña donde reunir al grupo, un comedor para las sobremesas largas y una cocina equipada para cocinar entre todos. Por eso es ideal para celebraciones, retiros y reuniones familiares.
            </p>
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-4 pb-20 md:px-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <img src={comedor} alt="Comedor con chimenea de piedra" width={1600} height={1067} loading="lazy" className="aspect-[4/3] w-full rounded-sm object-cover" />
          <img src={bano} alt="Baño privado con ducha de obra" width={1600} height={1067} loading="lazy" className="aspect-[4/3] w-full rounded-sm object-cover" />
        </div>
      </section>
      {habitaciones.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 pb-20 md:px-6">
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">Habitaciones</h2>
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            {habitaciones.map((h: { id: string; nombre: string; descripcion: string; capacidad: number; precioDesde: number | null; fotos: { url: string; alt: string }[] }) => (
              <article key={h.id} className="overflow-hidden rounded-sm border border-border bg-card">
                {h.fotos.length > 0 ? (
                  <Carousel className="w-full">
                    <CarouselContent>
                      {h.fotos.map((f: { url: string; alt: string }, i: number) => (
                        <CarouselItem key={i}>
                          <img
                            src={f.url}
                            alt={f.alt || h.nombre}
                            loading={i === 0 ? "eager" : "lazy"}
                            className="aspect-[4/3] w-full object-cover"
                          />
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {h.fotos.length > 1 && (
                      <>
                        <CarouselPrevious className="left-3" />
                        <CarouselNext className="right-3" />
                      </>
                    )}
                  </Carousel>
                ) : (
                  <div className="aspect-[4/3] w-full bg-muted" />
                )}
                <div className="p-5">
                  <h3 className="font-serif text-2xl text-foreground">{h.nombre}</h3>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Capacidad: {h.capacidad} {h.capacidad === 1 ? "persona" : "personas"}
                  </p>
                  {h.precioDesde != null && (
                    <p className="mt-2 text-sm font-medium text-foreground">Desde {h.precioDesde} €/noche</p>
                  )}
                  {h.descripcion && <p className="mt-3 text-sm text-muted-foreground">{h.descripcion}</p>}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
      <section className="bg-secondary/40 py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">Servicios incluidos</h2>
          <ul className="mt-8 grid gap-x-8 gap-y-3 text-foreground/85 sm:grid-cols-2 md:grid-cols-3">
            {servicios.map((s) => (
              <li key={s.id} className="flex items-start gap-2">
                <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                {s.nombre}
              </li>
            ))}
          </ul>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-4 py-20 md:px-6">
        <img src={exterior} alt="Exterior de la casa en El Ordial" width={1600} height={1067} loading="lazy" className="aspect-[21/9] w-full rounded-sm object-cover" />
      </section>

      <TarifasSection />
    </>
  );
}